import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { FileText, Search, Shield, Building, ArrowLeft, ExternalLink, Download } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const DocumentSearchPage = () => {
  const [files, setFiles] = useState([]);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCompetitorDocs, setShowCompetitorDocs] = useState(false);
  const navigate = useNavigate();

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Charger la liste des fichiers
  const loadFiles = useCallback(async () => {
    setIsLoadingFiles(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/list_files`);
      // Structure: ["nom_fichier", "concurrent_flag", "upload_date"]
      const fileList = response.data.files || [];
      const parsedFiles = fileList.map(item => {
        if (Array.isArray(item) && item.length >= 3) {
          return {
            name: item[0],
            isCompetitor: item[1] === "1",
            uploadDate: item[2]
          };
        } else if (Array.isArray(item) && item.length >= 2) {
          return {
            name: item[0],
            isCompetitor: item[1] === "1",
            uploadDate: null
          };
        }
        return null;
      }).filter(Boolean);
      
      setFiles(parsedFiles);
      setFilteredFiles(parsedFiles);
    } catch (error) {
      console.error('Erreur lors du chargement des fichiers:', error);
    } finally {
      setIsLoadingFiles(false);
    }
  }, [BACKEND_URL]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  // Ouvrir un fichier dans un nouvel onglet
  const handleOpenFile = (fileObj) => {
    try {
      const fileUrl = `${BACKEND_URL}/api/download?filename=${encodeURIComponent(fileObj.name)}`;
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Erreur ouverture:', error);
    }
  };

  // Télécharger un fichier
  const handleDownload = (filename) => {
    window.open(`${BACKEND_URL}/api/download?filename=${encodeURIComponent(filename)}`, '_blank');
  };

  // Filtrer les fichiers selon la recherche
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredFiles(files);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = files.filter(file => 
        file.name.toLowerCase().includes(query)
      );
      setFilteredFiles(filtered);
    }
  }, [searchQuery, files]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-slate-200 shadow-sm sticky top-0 z-50">
        <div className="px-4 sm:px-8 py-3 sm:py-4">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              size="sm"
              className="flex items-center gap-1.5 flex-shrink-0 px-2 sm:px-3"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Retour au chat</span>
            </Button>
            <div className="h-6 w-px bg-slate-300 hidden sm:block flex-shrink-0" />
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center shadow-lg flex-shrink-0">
                <Search className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-sm sm:text-xl font-bold text-slate-800 truncate">Recherche documentaire</h1>
                <p className="text-xs text-slate-600 hidden sm:block">Parcourir tous les documents disponibles</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-3 sm:px-8 py-4 sm:py-8 max-w-7xl mx-auto">
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center space-x-3">
                <FileText className="w-5 h-5 text-cyan-600 flex-shrink-0" />
                <CardTitle className="text-base sm:text-lg font-semibold text-slate-800">Fichiers disponibles</CardTitle>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center space-x-2">
                  <Label htmlFor="filter-switch" className="text-sm text-slate-600">
                    {showCompetitorDocs ? 'Concurrents' : 'Aprolis'}
                  </Label>
                  <Switch
                    id="filter-switch"
                    checked={showCompetitorDocs}
                    onCheckedChange={setShowCompetitorDocs}
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadFiles}
                  disabled={isLoadingFiles}
                  className="text-sm flex-shrink-0"
                >
                  {isLoadingFiles ? 'Chargement...' : 'Actualiser'}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Barre de recherche */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher un document par nom..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              {searchQuery && (
                <p className="text-xs text-slate-500 mt-2">
                  {filteredFiles.length} résultat{filteredFiles.length > 1 ? 's' : ''} trouvé{filteredFiles.length > 1 ? 's' : ''}
                </p>
              )}
            </div>

            {isLoadingFiles ? (
              <div className="text-center py-8 text-slate-500">
                Chargement des fichiers...
              </div>
            ) : filteredFiles.length === 0 && !searchQuery ? (
              <div className="text-center py-8 text-slate-500">
                Aucun fichier disponible
              </div>
            ) : (
              <div className="space-y-6">
                {/* Documents standards */}
                {(() => {
                  const standardDocs = filteredFiles.filter(file => !file.isCompetitor);
                  return !showCompetitorDocs && standardDocs.length > 0 && (
                    <div>
                      <div className="flex items-center space-x-2 mb-3">
                        <Shield className="w-4 h-4 text-blue-600" />
                        <h4 className="text-sm font-semibold text-slate-700">
                          Documents standards ({standardDocs.length})
                        </h4>
                        <Badge variant="secondary" className="bg-blue-100 text-blue-700 border-blue-200">
                          Aprolis
                        </Badge>
                      </div>
                      <div className="grid gap-2">
                        {standardDocs.map((file, index) => (
                          <Card key={`standard-${index}`} className="border border-blue-200 bg-blue-50/30">
                            <CardContent className="p-2.5 sm:p-3">
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                                  <div className="w-7 h-7 sm:w-8 sm:h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <span className="text-xs sm:text-sm font-medium text-slate-800 truncate block">
                                      {file.name}
                                    </span>
                                    <div className="flex items-center gap-2 mt-0.5 sm:mt-1">
                                      <Badge variant="outline" className="text-[10px] sm:text-xs bg-blue-50 border-blue-200 text-blue-700">
                                        Aprolis
                                      </Badge>
                                      {file.uploadDate && (
                                        <span className="text-[10px] sm:text-xs text-slate-500 hidden sm:inline">
                                          Chargé le {new Date(file.uploadDate).toLocaleDateString('fr-FR', { 
                                            year: 'numeric', 
                                            month: 'short', 
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                          })}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleOpenFile(file)}
                                    className="p-2 hover:bg-blue-100 border-blue-200"
                                    title="Ouvrir dans un nouvel onglet"
                                  >
                                    <ExternalLink className="w-4 h-4 text-blue-600" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDownload(file.name)}
                                    className="p-2 hover:bg-blue-100 border-blue-200"
                                    title="Télécharger"
                                  >
                                    <Download className="w-4 h-4 text-blue-600" />
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Documents concurrents */}
                {(() => {
                  const competitorDocs = filteredFiles.filter(file => file.isCompetitor);
                  return showCompetitorDocs && competitorDocs.length > 0 && (
                    <div>
                      <div className="flex items-center space-x-2 mb-3">
                        <Building className="w-4 h-4 text-orange-600" />
                        <h4 className="text-sm font-semibold text-slate-700">
                          Documents concurrents ({competitorDocs.length})
                        </h4>
                        <Badge variant="secondary" className="bg-orange-100 text-orange-700 border-orange-200">
                          Concurrence
                        </Badge>
                      </div>
                      <div className="grid gap-2">
                        {competitorDocs.map((file, index) => (
                          <Card key={`competitor-${index}`} className="border border-orange-200 bg-orange-50/30">
                            <CardContent className="p-2.5 sm:p-3">
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                                  <div className="w-7 h-7 sm:w-8 sm:h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-600" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <span className="text-xs sm:text-sm font-medium text-slate-800 truncate block">
                                      {file.name}
                                    </span>
                                    <div className="flex items-center gap-2 mt-0.5 sm:mt-1">
                                      <Badge variant="outline" className="text-[10px] sm:text-xs bg-orange-50 border-orange-200 text-orange-700">
                                        Concurrent
                                      </Badge>
                                      {file.uploadDate && (
                                        <span className="text-[10px] sm:text-xs text-slate-500 hidden sm:inline">
                                          Chargé le {new Date(file.uploadDate).toLocaleDateString('fr-FR', { 
                                            year: 'numeric', 
                                            month: 'short', 
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                          })}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleOpenFile(file)}
                                    className="p-2 hover:bg-orange-100 border-orange-200"
                                    title="Ouvrir dans un nouvel onglet"
                                  >
                                    <ExternalLink className="w-4 h-4 text-orange-600" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDownload(file.name)}
                                    className="p-2 hover:bg-orange-100 border-orange-200"
                                    title="Télécharger"
                                  >
                                    <Download className="w-4 h-4 text-orange-600" />
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Message si aucun document dans la catégorie sélectionnée ou recherche */}
                {(() => {
                  const standardDocs = filteredFiles.filter(file => !file.isCompetitor);
                  const competitorDocs = filteredFiles.filter(file => file.isCompetitor);
                  
                  // Message pour recherche sans résultat
                  if (searchQuery && filteredFiles.length === 0) {
                    return (
                      <div className="text-center py-8 text-slate-500">
                        <Search className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        <p>Aucun document trouvé pour "{searchQuery}"</p>
                        <button 
                          onClick={() => setSearchQuery('')} 
                          className="text-cyan-600 hover:underline text-sm mt-2"
                        >
                          Réinitialiser la recherche
                        </button>
                      </div>
                    );
                  }
                  
                  // Messages pour catégorie vide
                  if (!showCompetitorDocs && standardDocs.length === 0 && competitorDocs.length > 0) {
                    return (
                      <div className="text-center py-8 text-slate-500">
                        Aucun document Aprolis{searchQuery ? ' correspondant à votre recherche' : ''}. <button onClick={() => setShowCompetitorDocs(true)} className="text-cyan-600 hover:underline">Voir les documents concurrents</button>
                      </div>
                    );
                  }
                  
                  if (showCompetitorDocs && competitorDocs.length === 0 && standardDocs.length > 0) {
                    return (
                      <div className="text-center py-8 text-slate-500">
                        Aucun document concurrent{searchQuery ? ' correspondant à votre recherche' : ''}. <button onClick={() => setShowCompetitorDocs(false)} className="text-cyan-600 hover:underline">Voir les documents Aprolis</button>
                      </div>
                    );
                  }
                  
                  return null;
                })()}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DocumentSearchPage;
